
import React, { useState, useMemo } from 'react';
import { generateSalt, generateRecoveryKey, deriveKeyFromPassword, wrapKey, generateRandomMasterKey } from '../utils/crypto';
import { RECOVERY_SALT } from '../utils/crypto';

interface MasterKeySetupProps {
  onComplete: (key: string, salt: string, wrappedKey: string, passwordWrappedKey: string) => Promise<void>;
  onClose?: () => void;
}

const MasterKeySetup: React.FC<MasterKeySetupProps> = ({ onComplete, onClose }) => {
  const [step, setStep] = useState(1);
  const [key, setKey] = useState('');
  const [confirmKey, setConfirmKey] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [recoveryKey] = useState(() => generateRecoveryKey());
  const [copied, setCopied] = useState(false);

  // Requisitos detalhados
  const checks = useMemo(() => ({
    length: key.length >= 8,
    upper: /[A-Z]/.test(key),
    number: /[0-9]/.test(key),
    symbol: /[^A-Za-z0-9]/.test(key)
  }), [key]);

  const strength = useMemo(() => {
    if (!key) return 0;
    let score = 0;
    if (checks.length) score += 25;
    if (checks.upper) score += 25;
    if (checks.number) score += 25;
    if (checks.symbol) score += 25;
    return score;
  }, [key, checks]);

  const strengthConfig = useMemo(() => {
    if (strength <= 25) return { color: 'bg-red-500', label: 'Muito Fraca', text: 'text-red-500' };
    if (strength <= 50) return { color: 'bg-amber-500', label: 'Moderada', text: 'text-amber-500' };
    if (strength <= 75) return { color: 'bg-blue-500', label: 'Forte', text: 'text-blue-500' };
    return { color: 'bg-emerald-500', label: 'Inquebrável', text: 'text-emerald-500' };
  }, [strength]);

  const handleCopy = () => {
    navigator.clipboard.writeText(recoveryKey);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleFinish = async () => {
    setError(null);
    if (key !== confirmKey) {
      setError("As senhas não coincidem.");
      return;
    }
    if (strength < 75) {
      setError("Crie uma chave mais forte para sua proteção.");
      return;
    }
    
    setLoading(true);
    try {
      const masterDataKey = await generateRandomMasterKey();
      const newSalt = generateSalt();
      const passwordAccessKey = await deriveKeyFromPassword(key, newSalt);
      const recoveryAccessKey = await deriveKeyFromPassword(recoveryKey, RECOVERY_SALT);
      const wrappedByRecovery = await wrapKey(masterDataKey, recoveryAccessKey);
      const wrappedByPassword = await wrapKey(masterDataKey, passwordAccessKey);
      
      await onComplete(key, newSalt, wrappedByRecovery, wrappedByPassword);
    } catch (err: any) {
      setError("Falha ao configurar segurança.");
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[1000] bg-slate-900/95 backdrop-blur-md flex items-center justify-center p-4 md:p-6 animate-in fade-in duration-500 overflow-y-auto">
      <div className="bg-white dark:bg-slate-900 w-full max-w-md rounded-[2.5rem] p-6 md:p-10 shadow-2xl border border-blue-500/10 relative my-auto overflow-hidden">
        
        {onClose && (
          <button 
            onClick={onClose}
            className="absolute top-5 right-5 w-10 h-10 rounded-full bg-slate-100 dark:bg-slate-800 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 transition-colors flex items-center justify-center z-20 active:scale-90"
          >
            <i className="fa-solid fa-times"></i>
          </button>
        )}

        {step === 1 && (
          <div className="animate-in slide-in-from-bottom-4 relative z-10 text-center flex flex-col items-center">
            <div className="flex flex-col items-center mb-6">
              <div className="w-14 h-14 bg-blue-600 text-white rounded-[1.5rem] flex items-center justify-center shadow-xl shadow-blue-500/30 mb-4 rotate-3">
                <i className="fa-solid fa-shield-halved text-2xl"></i>
              </div>
              <div className="flex items-center gap-1.5">
                <span className="text-xl font-black tracking-tighter text-slate-800 dark:text-slate-100 uppercase">Equilibra</span>
                <span className="bg-blue-100 dark:bg-blue-900/40 text-blue-600 dark:text-blue-400 text-[7px] font-black px-1.5 py-0.5 rounded-full uppercase tracking-widest">Security</span>
              </div>
            </div>

            <h2 className="text-xl md:text-2xl font-black text-slate-900 dark:text-slate-50 mb-3 tracking-tight leading-tight uppercase">Soberania Digital</h2>
            <p className="text-slate-500 dark:text-slate-400 mb-8 leading-relaxed font-medium text-[11px] md:text-xs max-w-[300px]">Você está prestes a criar sua Chave Mestra. Ela é o único meio de ler seus dados. Sem ela, seus registros são apenas ruído digital ilegível.</p>

            <div className="w-full space-y-3 mb-4">
              <button onClick={() => setStep(2)} className="w-full bg-blue-600 text-white py-5 rounded-[1.5rem] font-black text-xs uppercase tracking-widest shadow-xl shadow-blue-500/20 active:scale-95 transition-all hover:bg-blue-700">Começar Configuração</button>
            </div>
            <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest">Tempo estimado: 2 minutos</p>
          </div>
        )}

        {step === 2 && (
          <div className="animate-in slide-in-from-right-4 relative z-10">
            <h3 className="text-xl font-black text-slate-900 dark:text-slate-50 tracking-tight leading-none mb-2 uppercase">Criar Chave Mestra</h3>
            <p className="text-slate-400 text-[10px] font-bold uppercase tracking-widest mb-8">Esta é a sua senha de decriptografia</p>
            
            <div className="space-y-6">
              <div className="space-y-3">
                <div className="flex justify-between items-end mb-1">
                  <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Sua Senha</label>
                  {key && <span className={`text-[9px] font-black uppercase tracking-widest ${strengthConfig.text}`}>{strengthConfig.label}</span>}
                </div>
                <input type="password" value={key} onChange={(e) => setKey(e.target.value)} className="w-full bg-slate-50 dark:bg-slate-800 border-none rounded-2xl px-6 py-5 text-sm font-bold outline-none focus:ring-2 focus:ring-blue-600 transition-all" placeholder="Digite sua chave..." />
                
                {/* BARRA DE COMPLEXIDADE */}
                <div className="h-1.5 w-full bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden flex gap-1">
                  <div className={`h-full transition-all duration-500 ${strength >= 25 ? strengthConfig.color : 'bg-transparent'}`} style={{ width: '25%' }}></div>
                  <div className={`h-full transition-all duration-500 ${strength >= 50 ? strengthConfig.color : 'bg-transparent'}`} style={{ width: '25%' }}></div>
                  <div className={`h-full transition-all duration-500 ${strength >= 75 ? strengthConfig.color : 'bg-transparent'}`} style={{ width: '25%' }}></div>
                  <div className={`h-full transition-all duration-500 ${strength >= 100 ? strengthConfig.color : 'bg-transparent'}`} style={{ width: '25%' }}></div>
                </div>

                {/* CHECKLIST DE REQUISITOS */}
                <div className="grid grid-cols-2 gap-2 mt-4">
                  <div className={`flex items-center gap-2 text-[8px] font-black uppercase tracking-widest ${checks.length ? 'text-emerald-500' : 'text-slate-400'}`}>
                    <i className={`fa-solid ${checks.length ? 'fa-circle-check' : 'fa-circle'}`}></i> 8+ Caracteres
                  </div>
                  <div className={`flex items-center gap-2 text-[8px] font-black uppercase tracking-widest ${checks.upper ? 'text-emerald-500' : 'text-slate-400'}`}>
                    <i className={`fa-solid ${checks.upper ? 'fa-circle-check' : 'fa-circle'}`}></i> Letra Maiúscula
                  </div>
                  <div className={`flex items-center gap-2 text-[8px] font-black uppercase tracking-widest ${checks.number ? 'text-emerald-500' : 'text-slate-400'}`}>
                    <i className={`fa-solid ${checks.number ? 'fa-circle-check' : 'fa-circle'}`}></i> Um Número
                  </div>
                  <div className={`flex items-center gap-2 text-[8px] font-black uppercase tracking-widest ${checks.symbol ? 'text-emerald-500' : 'text-slate-400'}`}>
                    <i className={`fa-solid ${checks.symbol ? 'fa-circle-check' : 'fa-circle'}`}></i> Um Símbolo
                  </div>
                </div>
              </div>

              <div className="space-y-3">
                <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Confirmar Senha</label>
                <input type="password" value={confirmKey} onChange={(e) => setConfirmKey(e.target.value)} className="w-full bg-slate-50 dark:bg-slate-800 border-none rounded-2xl px-6 py-5 text-sm font-bold outline-none focus:ring-2 focus:ring-blue-600 transition-all" placeholder="Repita a chave..." />
              </div>

              {error && <p className="text-red-500 text-[9px] font-black uppercase tracking-widest text-center animate-pulse">{error}</p>}

              <button 
                onClick={() => { 
                  if(strength >= 75 && key === confirmKey) setStep(3); 
                  else if (key !== confirmKey) setError("Senhas não coincidem.");
                  else setError("A chave precisa ser pelo menos 'Forte'.");
                }} 
                className={`w-full py-5 rounded-2xl font-black text-xs uppercase tracking-widest shadow-xl transition-all ${strength >= 75 && key === confirmKey ? 'bg-blue-600 text-white shadow-blue-500/20 active:scale-95' : 'bg-slate-100 dark:bg-slate-800 text-slate-400 cursor-not-allowed'}`}
              >
                Prosseguir para Backup
              </button>
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="animate-in slide-in-from-right-4 relative z-10">
            <h3 className="text-xl font-black text-slate-900 dark:text-slate-100 tracking-tight leading-none mb-2 uppercase">Chave de Backup</h3>
            <p className="text-slate-400 text-[10px] font-bold uppercase tracking-widest mb-8">Guarde isso em local físico e seguro</p>
            
            <div className="bg-slate-950 p-6 md:p-8 rounded-[2rem] border border-white/5 mb-8 text-center relative group">
              <p className="font-mono font-black text-sm md:text-base text-blue-400 tracking-widest mb-5 break-all leading-relaxed select-all">
                {recoveryKey}
              </p>
              <button onClick={handleCopy} className={`px-6 py-3 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all shadow-lg ${copied ? 'bg-emerald-600 text-white' : 'bg-blue-600 text-white active:scale-90'}`}>
                {copied ? <><i className="fa-solid fa-check mr-2"></i>Copiado</> : <><i className="fa-solid fa-copy mr-2"></i>Copiar Chave</>}
              </button>
            </div>

            <div className="bg-amber-50 dark:bg-amber-900/10 border border-amber-100 dark:border-amber-800/50 p-4 rounded-2xl flex gap-3 mb-8">
              <i className="fa-solid fa-triangle-exclamation text-amber-500 mt-0.5"></i>
              <p className="text-[9px] font-bold text-amber-700 dark:text-amber-400 leading-tight uppercase">
                Perder a senha e a chave de backup significa perda total dos seus dados. O suporte não pode recuperar seus dados por você.
              </p>
            </div>

            <button onClick={handleFinish} disabled={loading} className="w-full bg-slate-900 dark:bg-blue-600 text-white py-5 rounded-2xl font-black text-xs uppercase tracking-widest shadow-xl active:scale-95 transition-all">
              {loading ? <i className="fa-solid fa-circle-notch animate-spin mr-2"></i> : <i className="fa-solid fa-lock mr-2"></i>}
              {loading ? "Criptografando..." : "Finalizar e Proteger"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default MasterKeySetup;
